import React from 'react';
import PropTypes from 'prop-types';
import { getNewRelic, ACTION_NAME } from '../../analytics/newrelic';

/**
 * ErrorBoundary component that catches errors in its child components and displays a fallback UI.
 * @class
 * @param {Object} props - The properties passed to the component.
 * @param {ReactNode} props.children - The child components of ErrorBoundary.
 * @param {ReactElement} [props.fallbackUI] - The fallback UI to display when an error is caught.
 * @returns {ReactElement} The ErrorBoundary component.
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  /**
   * Lifecycle method that is called when an error is caught by the component.
   * @param {Error} error - The caught error.
   * @param {Object} info - Additional information about the error.
   */
  componentDidCatch(error, info) {
    this.setState({ hasError: true });
    // We don't know if the analytics context survives the error, so we are building up our own newRelic.error call
    const newRelic = getNewRelic();
    newRelic?.addPageAction(`${ACTION_NAME}`, {
      context: 'AppErrorBoundary',
      errorMessage: error.message,
      errorStack: error.stack,
      errorInfo: info,
      type: 'error',
    });
  }

  render() {
    if (this.state.hasError) {
      // Any custom fallback UI can be rendered here
      // Providing nothing causes react to render as much as it can by default
      // Ideally we would return a working cart and search here with something like this.props.fallbackUI
      return null;
    }
    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
};
